import { planLocalStorageItems, planLocalStorageManager } from "@iventis/local-storage/src/plan-local-storage";

/**
 * Creates a new Google Maps session using the provided API key and stores it in local storage
 * @param apiKey The Google Maps API key
 * @returns The Google Maps session token
 */
async function createGoogleMapSession(apiKey: string): Promise<string> {
    const response = await fetch(`https://tile.googleapis.com/v1/createSession?key=${apiKey}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mapType: "satellite", language: "en-GB", region: "GB" }),
    });
    const data = await response.json();

    if (!response.ok) {
        throw new Error(`Error creating Google Maps session: ${data.error.message}`);
    }
    const sessionToken = data.session;

    planLocalStorageManager.set(planLocalStorageItems.googleMapsSession, {
        apiKey,
        session: sessionToken,
        expiry: data.expiry,
    });

    return sessionToken;
}

/**
 * Gets the existing Google Maps session or creates a new one if none exists or it has expired
 * @param apiKey The Google Maps API key
 * @returns The Google Maps session token
 */
export async function getGoogleMapSession(apiKey: string): Promise<string> {
    let session: string | null = null;
    const currentGoogleMapSession = planLocalStorageManager.get(planLocalStorageItems.googleMapsSession);
    // Create new session if the user doesn't have one, the api key has changed or the session has expired
    if (!currentGoogleMapSession || apiKey !== currentGoogleMapSession.apiKey || currentGoogleMapSession.expiry < Math.floor(Date.now() / 1000)) {
        session = await createGoogleMapSession(apiKey);
    } else {
        // The user is already in a valid session
        session = currentGoogleMapSession.session;
    }
    return session;
}
